import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  RefreshCw,
  CheckCircle,
  Filter,
} from 'lucide-react';
import { useDevoluciones, useDeleteDevolucion } from '../../hooks/useDevoluciones';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

type EstadoDevolucion = 'todas' | 'pendiente' | 'aprobada' | 'rechazada' | 'completada';
type TipoDevolucion = 'todos' | 'devolucion' | 'cambio' | 'garantia';

const ESTADOS: { value: EstadoDevolucion; label: string; color: string }[] = [
  { value: 'todas', label: 'Todas', color: 'gray' },
  { value: 'pendiente', label: 'Pendiente', color: 'yellow' },
  { value: 'aprobada', label: 'Aprobada', color: 'blue' },
  { value: 'rechazada', label: 'Rechazada', color: 'red' },
  { value: 'completada', label: 'Completada', color: 'green' },
];

const TIPOS: { value: TipoDevolucion; label: string; icon: any }[] = [
  { value: 'todos', label: 'Todos', icon: null },
  { value: 'devolucion', label: 'Devolución', icon: RefreshCw },
  { value: 'cambio', label: 'Cambio', icon: RefreshCw },
  { value: 'garantia', label: 'Garantía', icon: null },
];

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    aprobada: { variant: 'info', label: 'Aprobada' },
    rechazada: { variant: 'danger', label: 'Rechazada' },
    completada: { variant: 'success', label: 'Completada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getTipoIcon = (tipo: string) => {
  switch (tipo) {
    case 'devolucion': return <RefreshCw size={14} className="text-blue-500" />;
    case 'cambio': return <RefreshCw size={14} className="text-green-500" />;
    default: return null;
  }
};

function useDebounce<T>(value: T, delay: number = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function DevolucionList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoDevolucion>('todas');
  const [tipoFiltro, setTipoFiltro] = useState<TipoDevolucion>('todos');
  
  const [fechaInicioLocal, setFechaInicioLocal] = useState('');
  const [fechaFinLocal, setFechaFinLocal] = useState('');

  const fechaInicio = useDebounce(fechaInicioLocal, 500);
  const fechaFin = useDebounce(fechaFinLocal, 500);

  const params: any = {};
  if (estadoFiltro !== 'todas') params.estado = estadoFiltro;
  if (tipoFiltro !== 'todos') params.tipo = tipoFiltro;
  if (fechaInicio) params.fechaInicio = fechaInicio;
  if (fechaFin) params.fechaFin = fechaFin;

  const { data: devoluciones, isLoading, isError, refetch } = useDevoluciones(params);
  const deleteDevolucion = useDeleteDevolucion();

  const devolucionesData = devoluciones || [];
  const devolucionesFiltradas = devolucionesData.filter((devolucion: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      devolucion.numeroDevolucion?.toLowerCase().includes(searchLower) ||
      devolucion.clienteNombre?.toLowerCase().includes(searchLower) ||
      devolucion.clienteDocumento?.toLowerCase().includes(searchLower) ||
      devolucion.motivo?.toLowerCase().includes(searchLower)
    );
  });

  const handleDelete = async (id: number, numero: string, estado: string) => {
    if (estado !== 'pendiente' && 
        estado !== 'aprobada' &&
        estado !== 'rechazada' && 
        estado !== 'completada') {
      toast.warning('Solo se pueden eliminar devoluciones en estado pendiente, aprobada, rechazada o completada');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar la devolución ${numero}?`)) return;

    try {
      await deleteDevolucion.mutateAsync(id);
      refetch();
    } catch (error: any) {
    }
  };

  const handleLimpiarFiltros = () => {
    setSearchTerm('');
    setEstadoFiltro('todas');
    setTipoFiltro('todos');
    setFechaInicioLocal('');
    setFechaFinLocal('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando devoluciones...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar las devoluciones
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Devoluciones</h1>
          <p className="text-sm text-gray-500">
            {devolucionesFiltradas.length} devolución(es) registradas
          </p>
        </div>
        <Link to="/devoluciones/nuevo">
          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus size={18} />
            Nueva Devolución
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por cliente, número o documento..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 min-w-[150px]">
            <Filter size={16} className="text-gray-400 flex-shrink-0" />
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value as EstadoDevolucion)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
            >
              {ESTADOS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-[150px]">
            <select
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value as TipoDevolucion)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleLimpiarFiltros}
            className="text-gray-500"
          >
            <Filter size={16} className="mr-2" />
            Limpiar
          </Button>
        </div>

        {/* Filtros de fecha */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha desde</label>
            <input
              type="date"
              value={fechaInicioLocal}
              onChange={(e) => setFechaInicioLocal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha hasta</label>
            <input
              type="date"
              value={fechaFinLocal}
              onChange={(e) => setFechaFinLocal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Lista de devoluciones */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {devolucionesFiltradas.map((devolucion: any) => (
          <div key={devolucion.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-blue-500" />
                  <h3 className="font-semibold text-gray-800">{devolucion.numeroDevolucion}</h3>
                  {getEstadoBadge(devolucion.estado)}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {getTipoIcon(devolucion.tipoDevolucion)}
                  <span className="text-xs text-gray-500 capitalize">
                    {devolucion.tipoDevolucion === 'devolucion' ? 'Devolución' :
                     devolucion.tipoDevolucion === 'cambio' ? 'Cambio' : 'Garantía'}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <Link
                  to={`/devoluciones/${devolucion.id}`}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                >
                  <Eye size={16} />
                </Link>
                {devolucion.estado === 'pendiente' && (
                  <Link
                    to={`/devoluciones/editar/${devolucion.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                )}
                {(devolucion.estado === 'pendiente' || 
                  devolucion.estado === 'aprobada' ||
                  devolucion.estado === 'rechazada' || 
                  devolucion.estado === 'completada') && (
                  <button
                    onClick={() => handleDelete(devolucion.id, devolucion.numeroDevolucion, devolucion.estado)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Cliente */}
            <p className="text-xs text-gray-400 mb-2">Cliente: {devolucion.clienteNombre}</p>

            {/* Detalles */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Productos</span>
                <span className="font-medium text-gray-700">
                  {devolucion.detalles?.length || 0} productos
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Monto</span>
                <span className="font-medium text-gray-700">
                  {formatCurrency(devolucion.montoTotal || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Fecha</span>
                <span className="text-xs text-gray-400">
                  {formatDate(devolucion.fechaDevolucion || devolucion.createdAt)}
                </span>
              </div>
              {devolucion.motivo && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Motivo</span>
                  <span className="text-xs text-gray-500 truncate max-w-[150px]">
                    {devolucion.motivo}
                  </span>
                </div>
              )}
            </div>

            {/* Acciones */}
            <div className="mt-3 pt-3 border-t border-gray-100">
              {devolucion.estado === 'pendiente' && (
                <Link
                  to={`/devoluciones/${devolucion.id}`}
                  className="w-full flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                >
                  <CheckCircle size={14} />
                  Gestionar
                </Link>
              )}
              {devolucion.estado !== 'pendiente' && (
                <Link
                  to={`/devoluciones/${devolucion.id}`}
                  className="w-full flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors"
                >
                  <Eye size={14} />
                  Ver detalles
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>

      {devolucionesFiltradas.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron devoluciones</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay devoluciones que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/devoluciones/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear la primera devolución
          </Link>
        </div>
      )}
    </div>
  );
}